import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth'
import { auth } from '../../firebase'
import { getAuthErrorMessage } from '../login/authFeedback'

export default function EmployeeLoginPage({ onSignedIn }) {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const inFlight = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (!user) return
      if (onSignedIn) { onSignedIn(user); return }
      let path = '/timesheet'
      try { if ((await user.getIdTokenResult()).claims?.role === 'accountant') path = '/artgold-admin' } catch {}
      if (mounted.current && auth.currentUser?.uid === user.uid) navigate(path, { replace: true })
    })
    return () => { mounted.current = false; unsubscribe() }
  }, [navigate, onSignedIn])
  const submit = async event => {
    event.preventDefault()
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true); setError('')
    try { await signInWithEmailAndPassword(auth, email.trim(), password) }
    catch (failure) { if (mounted.current) setError(getAuthErrorMessage(failure)) }
    finally {
      inFlight.current = false
      if (mounted.current) { setBusy(false); setPassword('') }
    }
  }
  return <main className="mx-auto flex min-h-[70vh] w-full max-w-4xl items-center px-4 py-8 sm:py-14">
    <section className="grid w-full overflow-hidden rounded-2xl border border-border bg-card shadow-sm md:grid-cols-2">
      <aside className="flex flex-col justify-between gap-8 bg-[#17191D] p-7 text-white sm:p-10">
        <p className="text-sm font-semibold tracking-[0.18em]">ARTGOLD <span className="font-normal text-white/60">TIMESHEET</span></p>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Your workday,<br />at a glance.</h1>
          <p className="mt-4 text-sm leading-6 text-white/70">Slide to clock in, clock out or manage your breaks. A photo is saved with each action.</p>
        </div>
        <p className="border-t border-white/15 pt-5 text-xs leading-5 text-white/60">Breaks are unpaid and tracked separately from working time.</p>
      </aside>
      <div className="p-7 sm:p-10">
        <h2 className="text-2xl font-semibold text-foreground">Employee sign in</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">Use your own email and password. Your time records are linked to your individual account.</p>
        <form className="mt-7 space-y-5" onSubmit={submit}>
          {error && <p role="alert" className="rounded-lg border border-destructive/25 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>}
          <label className="block text-sm font-medium text-foreground" htmlFor="employee-email">Email address
            <input id="employee-email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} required disabled={busy} value={email} onChange={event => { setEmail(event.target.value); setError('') }} className="mt-2 block w-full rounded-lg border border-border bg-background px-3 py-3 text-base" placeholder="you@artgoldjewelry.com" />
          </label>
          <label className="block text-sm font-medium text-foreground" htmlFor="employee-password">Password
            <input id="employee-password" type="password" autoComplete="current-password" required disabled={busy} value={password} onChange={event => { setPassword(event.target.value); setError('') }} className="mt-2 block w-full rounded-lg border border-border bg-background px-3 py-3 text-base" />
          </label>
          <button type="submit" disabled={busy} className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? 'Signing in…' : 'Sign in'}</button>
          <p className="border-t border-border pt-5 text-xs leading-5 text-muted-foreground">Need an account or help with your password? Contact Cinara.</p>
        </form>
      </div>
    </section>
  </main>
}
